'use client';

// 实现drawer组件
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { claimReward, useGetRewardPopupList } from '@/features/reward/services/rewardcenter';
import { Spin } from '@/components/ui-feat/spin';
import { isEmpty } from 'lodash-es';
import { rewardCardDrawerHelper } from './helpers/rewardCardDrawerHelper';
import { RewardStatusEnum, RewardTypeEnum } from '@/types/reward';
import { CashbackReward } from './base/cashback-reward';
import { CumulativeReward } from './base/cumulative-reward';
import PeriodicReward from './base/periodic-reward';
import { RankingReward } from './base/ranking-reward';
import { ReferralReward } from './base/refferral-reward';
import TurntableReward from './base/turntable-reward';
import { UnlockedReward } from './base/unlocked-reward';
import { VipUpgradeReward } from './base/vip-upgrade-reward';
import { CalenderReward } from './base/calender-reward';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import type { RewardDrawerCardItem } from './types';
import { WelcomeReward } from './base/welcome-reward';
import { showClaimDrawer } from '../claim-drawer';
import { Button } from '@/components/ui/button';
import { useRouter } from '@/i18n/navigation';
import { useRewardCountManager } from '../../store/reward-count';
import { useRewardCenterManager } from '../../store/reward-center-list';
import { useEffect } from 'react';
import Empty from '@/components/ui-feat/empty';
import { track } from '@/utils/track';
import { useCurrencyRate } from '@/features/ranking/hooks/useCurrencyRate';
import { RewardTypeTextMap } from '../../const/event';
import { useTranslations } from 'next-intl';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import toast from 'react-hot-toast';
import { RewardTypeHelper } from '../../helpers/reward-type';
import { LOGIN_SOURCE_MAP } from '@/const/report';
import IconFont from '@/components/ui/iconFont';

function getProgress(progressList: RewardDrawerCardItem['progressList'], type: 'Wagered' | 'Deposit' | 'Cash') {
  return (progressList || []).find((progress) => progress.type === type);
}

const RewardCardDrawer = NiceModal.create(() => {
  const modal = useModal();
  const { playAudio } = useAudioHelper({ id: AudioId.RewardReceived });
  const { data, isLoading, refetch } = useGetRewardPopupList({
    queryConfig: {
      enabled: false,
    }
  });
  const router = useRouter();
  const rewardCardList = isLoading || isEmpty(data) ? [] : rewardCardDrawerHelper.sortRewardCardData(data.list);
  const { updateClaimCount } = useRewardCountManager();
  const { updateCenterList } = useRewardCenterManager();
  const { getRate } = useCurrencyRate();
  const t = useTranslations('reward');
  const t_common = useTranslations('common');

  useEffect(() => {
    if (modal.visible) {
      refetch();  // 每次打开drawer时重新请求
      // 产品希望晚一点比click
      setTimeout(() => {
        track('reward_page_view', {
          page_type: LOGIN_SOURCE_MAP.RewardsPopup,
        });
      }, 100);
    }
  }, [modal.visible]);

  const onClose = () => {
    modal.hide();
  };

  const onClaim = async (item) => {
    if ([RewardTypeEnum.WELCOME_GIFT,
    RewardTypeEnum.CUMULATIVE_REWARD,
    RewardTypeEnum.TURNTABLE_REWARD,
    RewardTypeEnum.CASH_BACK,
    RewardTypeEnum.WEEKLY_REWARD,
    RewardTypeEnum.MONTHLY_REWARD].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'claim',
      });
    }

    try {
      const res = await claimReward({ params: { rewardCard: item, reward_type: 1 }, }); // 奖励弹窗传1
      if (item.type === RewardTypeEnum.RANKING_REWARD) {
        const currency = item.currency;
        const exchange = getRate({ currency, amount: item.amount });
        track('leaderboard_prize_claim', {
          'term': item.term,
          'leaderboard_time': item.date_timestamp,
          'claim_method': 'Manual Claim',
          'exchange_rate': Number(exchange?.rate),
          'reward_currency': item.currency,
          'reward_amount': Number(item.amount),
          'reward_usd_value': exchange?.exchange_amount,
        });
      }
      refetch();
      updateClaimCount();
      updateCenterList();
      playAudio();
      showClaimDrawer({
        amount: item.amount,
        currency: item.currency,
        claimAmount: item?.claimAmount,
        claimCurrency: item?.claimCurrency,
        rewards: RewardTypeHelper.isVipReward(item) ? item.rewards : [{
          money: {
            amount: item.claimAmount,
            currency: item.claimCurrency,
          },
          wallet: {
            type: item.claimType,
            water_multiple: item.bonusWagerMultiple,
          },
          type: item.type,
        }],
      });
      return res;
    } catch (error) {
      toast.error((error as any)?.response?.data?.msg || t_common('errors.networkError'));
      return;
    }
  };

  const onSpin = (item) => {
    if ([RewardTypeEnum.WELCOME_GIFT,
    RewardTypeEnum.CUMULATIVE_REWARD,
    RewardTypeEnum.TURNTABLE_REWARD,
    RewardTypeEnum.CASH_BACK,
    RewardTypeEnum.WEEKLY_REWARD,
    RewardTypeEnum.MONTHLY_REWARD].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'spin',
      });
    }
    modal.hide();
  };

  const onTurntable = async (item) => {
    const actionMap = {
      [RewardStatusEnum.INVITE_NOT_PARTICIPATE]: 'get cash',
      [RewardStatusEnum.INVITE]: 'invite',
      [RewardStatusEnum.SPIN]: 'spin',
      [RewardStatusEnum.CLAIM]: 'claim',
    };
    await track('reward_page_button_click', {
      page_type: 'Rewards Popup',
      reward_type: RewardTypeTextMap[item.type],
      button_name: actionMap[item.status]
    });
    modal.hide();
  };

  const onTimeOut = () => {
    refetch();
  };

  const onDeposit = (item) => {
    if ([RewardTypeEnum.WELCOME_GIFT,
    RewardTypeEnum.CUMULATIVE_REWARD,
    RewardTypeEnum.TURNTABLE_REWARD,
    RewardTypeEnum.CASH_BACK,
    RewardTypeEnum.WEEKLY_REWARD,
    RewardTypeEnum.MONTHLY_REWARD].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'deposit',
      });
    }
    modal.hide();
  };

  const handleJumpToReward = () => {
    modal.hide();
    router.push('/reward');
  };

  const renderRewardCardList = () => {
    if (isEmpty(rewardCardList)) {
      return <Empty className='h-full' />;
    }
    return rewardCardList.map((item, i) => {
      switch (item.type) {
        // 1.日历奖励
        case RewardTypeEnum.CALENDAR_REWARD:
          return <CalenderReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            onClaim={() => onClaim(item)}
            onTimeOut={() => onTimeOut()}
          />;
        // 2.VIP晋级奖励
        case RewardTypeEnum.PROMOTION_REWARD:
          return <VipUpgradeReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            onClaim={() => onClaim(item)}
          />;
        // 3.现金返回奖励
        case RewardTypeEnum.CASH_BACK:
          return <CashbackReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            onClaim={() => onClaim(item)}
            onTimeOut={() => onTimeOut()}
          />;
        // 4.每周奖励
        case RewardTypeEnum.WEEKLY_REWARD:
          return <PeriodicReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            wageredProgress={getProgress(item.progressList, 'Wagered')}
            onClaim={() => onClaim(item)}
            onTimeOut={() => onTimeOut()}
          />;
        // 5.每月奖励
        case RewardTypeEnum.MONTHLY_REWARD:
          return <PeriodicReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            wageredProgress={getProgress(item.progressList, 'Wagered')}
            onClaim={() => onClaim(item)}
            onTimeOut={() => onTimeOut()}
          />;
        // 6.每日奖励
        case RewardTypeEnum.DAILY_REWARD:
          return <PeriodicReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            wageredProgress={getProgress(item.progressList, 'Wagered')}
            onClaim={() => onClaim(item)}
            onTimeOut={() => onTimeOut()}
          />;
        // 7.累充奖励
        case RewardTypeEnum.CUMULATIVE_REWARD:
          return <CumulativeReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            depositProgress={getProgress(item.progressList, 'Deposit')}
            onSpin={() => onSpin(item)}
          />;
        // 8.奖励解锁
        case RewardTypeEnum.REWARD_UNLOCK:
          return <UnlockedReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            wageredProgress={getProgress(item.progressList, 'Wagered')}
            {...(item.rewardUnlock || {})}
            onClaim={() => onClaim(item)}
          />;
        // 9.裂变转盘奖励
        case RewardTypeEnum.TURNTABLE_REWARD:
          return <TurntableReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            cashProgress={getProgress(item.progressList, 'Cash')}
            onTurntable={() => onTurntable(item)}
          />;
        // 10.邀请奖励
        case RewardTypeEnum.INVITE_REWARD:
          return <ReferralReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            wageredProgress={getProgress(item.progressList, 'Wagered')}
            depositProgress={getProgress(item.progressList, 'Deposit')}
            onClaim={() => onClaim(item)}
          />;
        // 11.排行榜奖励
        case RewardTypeEnum.RANKING_REWARD:
          return <RankingReward
            key={i}
            {...item as any}
            amount={+(item.amount || 0)}
            onClaim={() => onClaim(item)}
          />;
        // 12.欢迎礼包
        case RewardTypeEnum.WELCOME_GIFT:
          return <WelcomeReward
            key={i}
            {...item as any}
            bonusPercentage={(item?.fluctuationCountdown || 0) > 0 ? item.bonusPercentage : item.giftPercentage}
            amount={+(item.amount || 0)}
            id={item?.id || 0}
            onDeposit={() => onDeposit(item)}
          />;
        default:
          return null;
      }
    });
  };

  return (
    <Drawer open={modal.visible} onOpenChange={onClose} direction="bottom">
      <DrawerContent className="bg-surface border-none rounded-t-[10px] text-white h-[80vh] flex flex-col">
        <DrawerHeader className="text-center pt-6 pb-4">
          <DrawerTitle className="text-white font-semibold text-base">
            {t('availableRewards')}
          </DrawerTitle>
          <DrawerClose asChild className="absolute right-5 before:content-[''] before:absolute before:w-[34px] before:h-[34px] before:-left-[10.5px] before:-top-[10.5px]">
            <button className="rounded-full hover:bg-zinc-800 transition-colors  active:scale-90">
              <IconFont name="close" size={13} />
            </button>
          </DrawerClose>
        </DrawerHeader>
        <div className='flex-1 overflow-hidden overflow-y-auto no-scrollbar flex flex-col px-3'>
          <div className='flex-1 overflow-hidden overflow-y-auto no-scrollbar space-y-2.5'>
            {isLoading && <Spin className='h-full' />}
            {!isLoading && renderRewardCardList()}
          </div>
          <Button className='w-full my-7' variant='primary' onClick={handleJumpToReward}>{t('viewAll')}</Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
});

RewardCardDrawer.displayName = 'RewardCardDrawer';

export default RewardCardDrawer;